import React, { useEffect, useState } from 'react';
import { useRespondToRequestMutation } from '../../../redux/apis/endpoints/messageApi';
import axios from 'axios';
import { BACKEND_URL } from '../../../utils/config';
import ProductPreview from './ProductPreview';

const SwapRequestMessage = ({ message, isCurrentUser }) => {
  const [respondToSwap, { isLoading }] = useRespondToRequestMutation();
  const [requestedProduct, setRequestedProduct] = useState(null);
  const [offeredProduct, setOfferedProduct] = useState(null);
  const [isLoading1, setIsLoading1] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [error, setError] = useState(null);
  
  const requestedProductId = message.requestedProduct || 
    (message.status && message.status.requestedProduct);
  const offeredProductId = message.offeredProduct || 
    (message.status && message.status.offeredProduct);
  
  const isPending = !message.status || !message.status.responded;
  const isAccepted = message.status && message.status.accepted;
  
  // Fetch product details if they're not included directly
  useEffect(() => {
    const fetchProducts = async () => {
      if (requestedProductId && !requestedProduct) {
        setIsLoading1(true);
        try {
          const response = await axios.get(`${BACKEND_URL}/products/${requestedProductId}`);
          setRequestedProduct(response.data);
        } catch (err) {
          setError('Failed to load product details');
          console.error('Error loading requested product:', err);
        } finally {
          setIsLoading1(false);
        }
      }
      
      if (offeredProductId && !offeredProduct) {
        setIsLoading2(true);
        try {
          const response = await axios.get(`${BACKEND_URL}/products/${offeredProductId}`);
          setOfferedProduct(response.data);
        } catch (err) {
          setError('Failed to load product details');
          console.error('Error loading offered product:', err);
        } finally {
          setIsLoading2(false);
        }
      }
    };
    
    fetchProducts();
  }, [requestedProductId, offeredProductId, requestedProduct, offeredProduct]);
  
  const handleAccept = async () => {
    if (isLoading) return;
    try {
      await respondToSwap({
        messageId: message.id,
        accepted: true,
        responseMessage: 'Swap request accepted'
      }).unwrap();
    } catch (err) {
      console.error('Failed to accept swap:', err);
    }
  };
  
  const handleDecline = async () => {
    if (isLoading) return;
    try {
      await respondToSwap({
        messageId: message.id,
        accepted: false,
        responseMessage: 'Swap request declined'
      }).unwrap();
    } catch (err) {
      console.error('Failed to decline swap:', err);
    }
  };
  
  if (isLoading1 || isLoading2) {
    return (
      <div className="swap-message-loading p-3 bg-white rounded-md border border-gray-200">
        <div className="text-sm text-gray-600">Loading swap details...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="swap-message-error p-3 bg-white rounded-md border border-gray-200">
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }
  
  return (
    <div className="swap-message">
      <div className="text-sm font-medium text-indigo-600 mb-2">
        {isCurrentUser ? 'You requested a swap' : 'Swap request received'}
      </div>
      
      <div className="mt-2 p-3 bg-white rounded-md border border-gray-200">
        <div className="flex flex-col space-y-3">
          {isCurrentUser ? (
            <>
              <div className="text-xs font-medium text-gray-600">Your item:</div>
              {offeredProduct && <ProductPreview product={offeredProduct} isSmall />}
              
              <div className="text-xs font-medium text-gray-600 mt-2">For this item:</div>
              {requestedProduct && <ProductPreview product={requestedProduct} isSmall />}
            </>
          ) : (
            <>
              <div className="text-xs font-medium text-gray-600">They want to swap:</div>
              {offeredProduct && <ProductPreview product={offeredProduct} isSmall />}
              
              <div className="text-xs font-medium text-gray-600 mt-2">For your item:</div>
              {requestedProduct && <ProductPreview product={requestedProduct} isSmall />}
            </>
          )}
        </div>
        
        <div className="text-xs text-gray-500 mt-3">
          {message.messageText}
        </div>
        
        {/* Status indicator */}
        {!isCurrentUser && isPending && (
          <div className="mt-3 flex justify-end space-x-2">
            <button
              type="button"
              onClick={handleDecline}
              disabled={isLoading}
              className="px-3 py-1 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={isLoading}
              className="px-3 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Accept
            </button>
          </div>
        )}
        
        {!isPending && (
          <div className={`mt-2 text-xs font-medium ${isAccepted ? 'text-green-600' : 'text-red-600'}`}>
            {isAccepted ? 'Swap accepted' : 'Swap declined'}
          </div>
        )}
      </div>
    </div>
  );
};

export default SwapRequestMessage; 